import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { breadcrumbsActions } from "@store/breadcrumbs";
import ApplicationPageTemplate from "../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate";
import { useNavigate, useParams } from "react-router-dom";
import { getAlertById, smartAlertsActions, updateAlert, addAlert } from "@store/smart-alerts";
import { Form } from "react-final-form";
import { TextField, Select } from "mui-rff";
import { Alert, Button, Grid, MenuItem } from "@mui/material";
import { Box } from "@mui/system";
import { CHECK_FREQ, GROUP_BY_LIST, LOG_LEVEL, TYPE_LIST, THRESHOLD_CHECK, LOG_NAME_LIST, RECORD_LAST_MSG } from "../../utils/constants";
import { FORM_ERROR } from "final-form";
import { showSuccessMessage } from "@store/dashboard";
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

const initFormValue = {
	title: "",
	description: "",
	chilliGroup: "",
	chilliGroupTopic: "",
	activeStartTime: "00:00",
	activeEndTime: "00:00",
	type: "StackDriverLogBasedSmartAlert",
	status: true,
	details: {
		logName: "STDOUT",
		logLevel: "",
		loggerNamePattern: "",
		groupBy: "",
		threshold: "",
		thresholdCheck: "",
		messagePattern: "",
		recordLastMessage: "False",
		sqlQuery: "",
	},
};

const AlertForm = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [formValue, setFormValue] = useState(initFormValue);
	const selectedAlert = useSelector((state) => state.smartAlerts.selectedAlert);
	const { id } = useParams();
	const [isFetchingData, setIsFetchingData] = useState(true);
  const [openDialog, setOpenDialog] = useState({ show: false });

	useEffect(() => {
		dispatch(breadcrumbsActions.showMenu(true));
		if (id !== undefined) {
			dispatch(
				breadcrumbsActions.setBreadcrumbs([
					{ label: "Home", url: "/" },
					{ label: "Smart Alerts", url: "/smart-alerts" },
					{ label: "Edit Alert " + id, url: "/smart-alerts/edit/" + id },
				])
			);
			setIsFetchingData(true);
			dispatch(getAlertById({ alertId: id })).then(() => {
				setIsFetchingData(false);
			});
		} else {
			dispatch(
				breadcrumbsActions.setBreadcrumbs([
					{ label: "Home", url: "/" },
					{ label: "Smart Alerts", url: "/smart-alerts" },
					{ label: "New Alert", url: "/smart-alerts/add" },
				])
			);
			dispatch(smartAlertsActions.removeSelectedAlert());
			setIsFetchingData(false);
		}
	}, [id, dispatch]);

	useEffect(() => {
		if (selectedAlert) {
			setFormValue({
				...selectedAlert,
				status: selectedAlert.status === "DISABLED" ? false : true,
			});
		} else {
			setFormValue(initFormValue);
		}
	}, [selectedAlert]);

	function onSubmit(values) {
		// Remove blank details
		const newDetails = {};
		Object.keys(values.details).forEach((key) => {
			if (values.details[key] && values.details[key] !== null) {
				newDetails[key] = values.details[key];
			}
		});

		const payload = {
			...values,
			status: formValue.status ? "ENABLED" : "DISABLED",
			details: { ...newDetails },
		};
		return dispatch(
			selectedAlert !== undefined
				? updateAlert({
						formValues: payload,
				  })
				: addAlert({
						formValues: payload,
				  })
		).then((data) => {
			if (data.payload && data.payload.data && data.payload.status === "OK") {
				dispatch(showSuccessMessage("Alert " + (selectedAlert !== undefined ? "Updated" : "Added") + " Successfully:"));
				navigate("/smart-alerts/all");
			} else return { [FORM_ERROR]: data.error.message  };
		});
	}

	function validate(values) {
		let errors = {};
		if (!values.title) errors.title = "Title is mandatory and cannot be empty";
		if (!values.description) errors.description = "Description is mandatory and cannot be empty";
		if (!values.chilliGroup) errors.chilliGroup = "Chilli Group is mandatory and cannot be empty";
		if (!values.chilliGroupTopic) errors.chilliGroupTopic = "Chilli Group Topic is mandatory and cannot be empty";
		if (!values.activeStartTime) errors.activeStartTime = "Active Time is mandatory and cannot be empty";
		if (!values.activeEndTime) errors.activeEndTime = "Active Time is mandatory and cannot be empty";
		if (!values.checkFrequency) errors.checkFrequency = "Check Frequency is mandatory and cannot be empty";
		if (!values.type) errors.type = "Type is mandatory and cannot be empty";

		if (values.type === "StackDriverLogBasedSmartAlert") {
			errors.details = {};
			if (!values.details.logName) errors.details.logName = "Log Name is mandatory and cannot be empty";
			if (!values.details.recordLastMessage) errors.details.recordLastMessage = "Record Last Message is mandatory and cannot be empty";
			if (values.logName === "STDOUT") {
				if (!values.details.logLevel) errors.details.logLevel = "Log Level is mandatory and cannot be empty";
				if (!values.details.loggerNamePattern) errors.details.loggerNamePattern = "Logger Name Pattern is mandatory and cannot be empty";
				if (!values.details.messagePattern) errors.details.messagePattern = "Message Pattern is mandatory and cannot be empty";
				if (!values.details.groupBy) errors.details.groupBy = "Group By is mandatory and cannot be empty";
				if (!values.details.threshold) errors.details.threshold = "Threshold is mandatory and cannot be empty";
				if (!values.details.thresholdCheck) errors.details.thresholdCheck = "Threshold Check is mandatory and cannot be empty";
			} else if (values.logName === "LOAD_BALANCER") {
				if (!values.details.urlPattern) errors.details.urlPattern = "URL Pattern is mandatory and cannot be empty";
				if (!values.details.latencyThreshold) errors.details.latencyThreshold = "Latency Threshold is mandatory and cannot be empty";
			} else if (values.logName === "K8S_AUTO_SCALER") {
				if (!values.details.messagePattern) errors.details.messagePattern = "Message Pattern is mandatory and cannot be empty";
			} else if (values.logName === "SLOW_SQL_QUERY") {
				if (!values.details.messagePattern) errors.details.messagePattern = "Message Pattern is mandatory and cannot be empty";
			}
		}
		return errors;
	}

	  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

	return (
		<ApplicationPageTemplate title={id !== undefined ? "Edit Alert: " + id : "Add Alert"} description={id !== undefined ? "Modify Alert configuration and click update to reflect" : "Configure new Alert by selection filling the form fields and click Save"} pageReady={!isFetchingData}>
			{!isFetchingData && (
				<Form
					onSubmit={onSubmit}
					initialValues={formValue}
					validate={validate}
					render={({ submitError, handleSubmit, values }) => (
			<form
			  onSubmit={(e) =>
				process.env.REACT_APP_ENV !== 'production'
				  ? handleSubmit(e)
				  : handlePreSubmit(e)
			  }
			  noValidate>
			  <ProdConfirmationDialog
				openDialog={openDialog}
				yesHandler={handleSubmit}
			  />							<Grid container spacing={5}>
								<Grid item xs={6}>
									<TextField label="Title" name="title" required={true} variant="filled" />
								</Grid>
								<Grid item xs={6}>
									<TextField label="Description" name="description" required={true} variant="filled" />
								</Grid>
								<Grid item xs={6}>
									<TextField label="Chilli Group" name="chilliGroup" required={true} variant="filled" />
								</Grid>
								<Grid item xs={6}>
									<TextField label="Chilli Group Topic" name="chilliGroupTopic" required={true} variant="filled" />
								</Grid>
								<Grid item xs={6}>
									<Select name="checkFrequency" label="Check Frequency" variant="filled">
										{CHECK_FREQ.map((item, index) => (
											<MenuItem key={index} value={item.value}>
												{item.label}
											</MenuItem>
										))}
									</Select>
								</Grid>
								<Grid item xs={6}>
									<Grid container spacing={5}>
										<Grid item xs={6}>
											<TextField label="Active Between (HH:MM)" name="activeStartTime" required={true} variant="filled" />
										</Grid>
										<Grid item xs={6}>
											<TextField label="And (HH:MM)" name="activeEndTime" required={true} variant="filled" />
										</Grid>
									</Grid>
								</Grid>
								<Grid item xs={6}>
									<Select name="type" label="Type" variant="filled">
										<MenuItem value={""}>None</MenuItem>
										{TYPE_LIST.map((item, index) => (
											<MenuItem key={index} value={item.value}>
												{item.label}
											</MenuItem>
										))}
									</Select>
								</Grid>
								{values["type"] !== undefined && values["type"] === "StackDriverLogBasedSmartAlert" && (
									<>
										<Grid item xs={6}>
											<Select name="details.logName" label="Log Name" variant="filled">
												{LOG_NAME_LIST.map((item, index) => (
													<MenuItem key={index} value={item.value}>
														{item.label}
													</MenuItem>
												))}
											</Select>
										</Grid>
										<Grid item xs={6}>
											<Select name="details.recordLastMessage" label="Record Last Message" variant="filled">
												{RECORD_LAST_MSG.map((item, index) => (
													<MenuItem key={index} value={item.value}>
														{item.label}
													</MenuItem>
												))}
											</Select>
										</Grid>
										<Grid item xs={6}>
											<Select name="details.groupBy" label="Group By" variant="filled">
												{GROUP_BY_LIST.map((item, index) => (
													<MenuItem key={index} value={item.value}>
														{item.label}
													</MenuItem>
												))}
											</Select>
										</Grid>
										<Grid item xs={3}>
											<Select name="details.thresholdCheck" label="Threshold Check" variant="filled">
												{THRESHOLD_CHECK.map((item, index) => (
													<MenuItem key={index} value={item.value}>
														{item.label}
													</MenuItem>
												))}
											</Select>
										</Grid>
										<Grid item xs={3}>
											<TextField label="Threshold" name="details.threshold" required={true} variant="filled" />
										</Grid>
										{values["details"]["logName"] === "K8S_AUTO_SCALER" && (
											<>
												<Grid item xs={6}>
													<TextField label="Message Pattern" name="details.messagePattern" required={true} variant="filled" />
												</Grid>
											</>
										)}
										{values["details"]["logName"] === "SLOW_SQL_QUERY" && (
											<>
												<Grid item xs={6}>
													<TextField label="Message Pattern" name="details.messagePattern" required={true} variant="filled" />
												</Grid>
											</>
										)}
										{values["details"]["logName"] === "LOAD_BALANCER" && (
											<>
												<Grid item xs={6}>
													<TextField label="URL Pattern" name="details.urlPattern" required={true} variant="filled" />
												</Grid>
												<Grid item xs={6}>
													<TextField label="Latency Threshold" name="details.latencyThreshold" required={true} variant="filled" />
												</Grid>
											</>
										)}
										{values["details"]["logName"] === "STDOUT" && (
											<>
												<Grid item xs={6}>
													<Select name="details.logLevel" label="Log Level" variant="filled">
														{LOG_LEVEL.map((item, index) => (
															<MenuItem key={index} value={item.value}>
																{item.label}
															</MenuItem>
														))}
													</Select>
												</Grid>
												<Grid item xs={6}>
													<TextField label="Logger Name Pattern" name="details.loggerNamePattern" required={true} variant="filled" />
												</Grid>
												<Grid item xs={6}>
													<TextField label="Message Pattern" name="details.messagePattern" required={true} variant="filled" />
												</Grid>
											</>
										)}
									</>
								)}

								{values["type"] !== undefined && values["type"] === "SQLBasesSmartAlert" && (
									<>
										<Grid item xs={3}>
											<Select name="details.thresholdCheck" label="Threshold Check" variant="filled">
												{THRESHOLD_CHECK.map((item, index) => (
													<MenuItem key={index} value={item.value}>
														{item.label}
													</MenuItem>
												))}
											</Select>
										</Grid>
										<Grid item xs={3}>
											<TextField label="Threshold" name="details.threshold" required={true} variant="filled" />
										</Grid>
										<Grid item xs={12}>
											<TextField label="SQLQuery" name="details.sqlQuery" required={true} variant="filled" />
										</Grid>
									</>
								)}
								<Grid item xs={12}>
									{submitError && <Alert severity="error">{submitError}</Alert>}
								</Grid>
							</Grid>
							<Box sx={{ display: "flex", justifyContent: "flex-end", columnGap: "5px", marginTop: "20px" }}>
								<Button variant="contained" type="submit">
									Submit
								</Button>
							</Box>
						</form>
					)}
				/>
			)}
		</ApplicationPageTemplate>
	);
};

export default AlertForm;
